import {
  VibeAuditAppEdgeUnroutedPayloadSchema,
  VibeAuditAppEdgeUnroutedReadPayloadSchema
} from "./payloads/app-edge.schema";
import {
  VibeAuditApprovalExpiredPayloadSchema,
  VibeAuditDeploymentApprovedPayloadSchema,
  VibeAuditDeploymentRejectedPayloadSchema
} from "./payloads/approval-gate.schema";
import {
  VibeAuditBuildJobFailedPayloadSchema,
  VibeAuditBuildJobLostPayloadSchema,
  VibeAuditBuildJobRequeuedPayloadSchema,
  VibeAuditBuildJobRequeuedReadPayloadSchema,
  VibeAuditBuildJobSucceededPayloadSchema,
  VibeAuditBuildJobTimedOutPayloadSchema
} from "./payloads/build-job.schema";
import {
  VibeAuditCapacityApprovedPayloadSchema,
  VibeAuditCapacityApprovedReadPayloadSchema,
  VibeAuditCapacityExpiredPayloadSchema,
  VibeAuditCapacityExpiredReadPayloadSchema,
  VibeAuditCapacityGrownPayloadSchema,
  VibeAuditCapacityGrownReadPayloadSchema,
  VibeAuditCapacityRejectedPayloadSchema,
  VibeAuditCapacityRejectedReadPayloadSchema,
  VibeAuditCapacityRequestedPayloadSchema,
  VibeAuditCapacityRequestedReadPayloadSchema
} from "./payloads/capacity.schema";
import {
  VibeAuditCardBindingRemovedPayloadSchema,
  VibeAuditCardBindingRemovedReadPayloadSchema,
  VibeAuditCardBindingRenamedPayloadSchema,
  VibeAuditCardBindingRenamedReadPayloadSchema,
  VibeAuditCardGrantActivatedPayloadSchema,
  VibeAuditCardGrantActivatedReadPayloadSchema,
  VibeAuditCardGrantIssuedPayloadSchema,
  VibeAuditCardGrantIssuedReadPayloadSchema,
  VibeAuditCardGrantPausedPayloadSchema,
  VibeAuditCardGrantPausedReadPayloadSchema,
  VibeAuditCardGrantRevokedPayloadSchema,
  VibeAuditCardGrantRevokedReadPayloadSchema
} from "./payloads/card-grant.schema";
import {
  VibeAuditCostSafetyAutoSuspendedPayloadSchema,
  VibeAuditCostSafetyAutoSuspendedReadPayloadSchema,
  VibeAuditCostSafetyManuallyResumedPayloadSchema,
  VibeAuditCostSafetyManuallyResumedReadPayloadSchema,
  VibeAuditCostSafetyManuallySuspendedPayloadSchema,
  VibeAuditCostSafetyManuallySuspendedReadPayloadSchema,
  VibeAuditCostSafetyManuallyWarnedPayloadSchema,
  VibeAuditCostSafetyManuallyWarnedReadPayloadSchema,
  VibeAuditCostSafetySoftLimitClearedPayloadSchema,
  VibeAuditCostSafetySoftLimitWarningPayloadSchema,
  VibeAuditCostSafetySoftLimitWarningReadPayloadSchema
} from "./payloads/cost-safety.schema";
import {
  VibeAuditDeploymentBuildSucceededPayloadSchema,
  VibeAuditDeploymentBuildSucceededReadPayloadSchema,
  VibeAuditDeploymentCancelledPayloadSchema,
  VibeAuditDeploymentCancelledReadPayloadSchema,
  VibeAuditDeploymentDisplacedPayloadSchema,
  VibeAuditDeploymentDisplacedReadPayloadSchema,
  VibeAuditDeploymentFailedPayloadSchema,
  VibeAuditDeploymentFailedReadPayloadSchema,
  VibeAuditDeploymentHealthyPayloadSchema,
  VibeAuditDeploymentHealthyReadPayloadSchema,
  VibeAuditDeploymentServedPayloadSchema,
  VibeAuditDeploymentServedReadPayloadSchema,
  VibeAuditDeploymentSupersededPayloadSchema,
  VibeAuditDeploymentSupersededReadPayloadSchema,
  VibeAuditDeploymentTriggeredPayloadSchema,
  VibeAuditDeploymentTriggeredReadPayloadSchema
} from "./payloads/deployment.schema";
import {
  VibeAuditDeploymentRolledBackCostSafetyPayloadSchema,
  VibeAuditDeploymentRolledBackCostSafetyReadPayloadSchema,
  VibeAuditDeploymentRolledBackCrashLoopPayloadSchema,
  VibeAuditDeploymentRolledBackCrashLoopReadPayloadSchema,
  VibeAuditDeploymentRolledBackFailedSmokePayloadSchema,
  VibeAuditDeploymentRolledBackFailedSmokeReadPayloadSchema,
  VibeAuditDeploymentRolledBackHealthCheckPayloadSchema,
  VibeAuditDeploymentRolledBackHealthCheckReadPayloadSchema,
  VibeAuditDeploymentRolledBackUserPayloadSchema
} from "./payloads/deployment-rollback.schema";
import {
  VibeAuditGitPushNoDeployPayloadSchema,
  VibeAuditGitPushNoDeployReadPayloadSchema
} from "./payloads/git-push.schema";
import {
  VibeAuditSecretValueStagedPayloadSchema,
  VibeAuditSecretValueStagedReadPayloadSchema,
  VibeAuditSecretValueWrittenPayloadSchema,
  VibeAuditSecretValueWrittenReadPayloadSchema
} from "./payloads/secret-value.schema";
import {
  VibeAuditDeploymentVerificationOverriddenPayloadSchema,
  VibeAuditDeploymentVerificationRefusedPayloadSchema,
  VibeAuditDeploymentVerificationWarnedPayloadSchema
} from "./payloads/ship-gate.schema";

/**
 * The audit payload branches, grouped by how they are READ. Internal to this
 * folder and not re-exported from `index.ts`: `payload.schema.ts` composes the
 * emitted union from these lists and `read-payload.schema.ts` the read union, so
 * the two are built from one set of lists and cannot disagree about which event
 * types exist.
 */

/** Every branch whose read and emitted shapes are the same schema: none of them carries an enum field. */
export const VIBE_AUDIT_PAYLOAD_BRANCHES_READ_AS_EMITTED = [
  VibeAuditDeploymentApprovedPayloadSchema,
  VibeAuditDeploymentRejectedPayloadSchema,
  VibeAuditApprovalExpiredPayloadSchema,
  VibeAuditCostSafetySoftLimitClearedPayloadSchema,
  VibeAuditBuildJobSucceededPayloadSchema,
  VibeAuditBuildJobFailedPayloadSchema,
  VibeAuditBuildJobTimedOutPayloadSchema,
  VibeAuditBuildJobLostPayloadSchema,
  VibeAuditDeploymentRolledBackUserPayloadSchema,
  VibeAuditDeploymentVerificationRefusedPayloadSchema,
  VibeAuditDeploymentVerificationOverriddenPayloadSchema,
  VibeAuditDeploymentVerificationWarnedPayloadSchema
] as const;

/**
 * The branches that carry an enum field, as emitted (strict). Position `i` here
 * and in `VIBE_AUDIT_PAYLOAD_READ_COUNTERPARTS` is the same event type; the types
 * package's specs refuse a pair that disagrees and an event type either union
 * has that the other lacks.
 */
export const VIBE_AUDIT_PAYLOAD_BRANCHES_WITH_READ_TWIN = [
  VibeAuditDeploymentTriggeredPayloadSchema,
  VibeAuditDeploymentBuildSucceededPayloadSchema,
  VibeAuditDeploymentFailedPayloadSchema,
  VibeAuditDeploymentHealthyPayloadSchema,
  VibeAuditDeploymentSupersededPayloadSchema,
  VibeAuditDeploymentDisplacedPayloadSchema,
  VibeAuditDeploymentCancelledPayloadSchema,
  VibeAuditDeploymentServedPayloadSchema,
  VibeAuditDeploymentRolledBackCostSafetyPayloadSchema,
  VibeAuditDeploymentRolledBackHealthCheckPayloadSchema,
  VibeAuditDeploymentRolledBackCrashLoopPayloadSchema,
  VibeAuditDeploymentRolledBackFailedSmokePayloadSchema,
  VibeAuditBuildJobRequeuedPayloadSchema,
  VibeAuditCostSafetyAutoSuspendedPayloadSchema,
  VibeAuditCostSafetySoftLimitWarningPayloadSchema,
  VibeAuditCostSafetyManuallySuspendedPayloadSchema,
  VibeAuditCostSafetyManuallyWarnedPayloadSchema,
  VibeAuditCostSafetyManuallyResumedPayloadSchema,
  VibeAuditCapacityRequestedPayloadSchema,
  VibeAuditCapacityApprovedPayloadSchema,
  VibeAuditCapacityRejectedPayloadSchema,
  VibeAuditCapacityExpiredPayloadSchema,
  VibeAuditCapacityGrownPayloadSchema,
  VibeAuditAppEdgeUnroutedPayloadSchema,
  VibeAuditSecretValueStagedPayloadSchema,
  VibeAuditSecretValueWrittenPayloadSchema,
  VibeAuditGitPushNoDeployPayloadSchema,
  VibeAuditCardGrantIssuedPayloadSchema,
  VibeAuditCardGrantPausedPayloadSchema,
  VibeAuditCardGrantActivatedPayloadSchema,
  VibeAuditCardGrantRevokedPayloadSchema,
  VibeAuditCardBindingRenamedPayloadSchema,
  VibeAuditCardBindingRemovedPayloadSchema
] as const;

/** The read twins of the list above, in the same order. */
export const VIBE_AUDIT_PAYLOAD_READ_COUNTERPARTS = [
  VibeAuditDeploymentTriggeredReadPayloadSchema,
  VibeAuditDeploymentBuildSucceededReadPayloadSchema,
  VibeAuditDeploymentFailedReadPayloadSchema,
  VibeAuditDeploymentHealthyReadPayloadSchema,
  VibeAuditDeploymentSupersededReadPayloadSchema,
  VibeAuditDeploymentDisplacedReadPayloadSchema,
  VibeAuditDeploymentCancelledReadPayloadSchema,
  VibeAuditDeploymentServedReadPayloadSchema,
  VibeAuditDeploymentRolledBackCostSafetyReadPayloadSchema,
  VibeAuditDeploymentRolledBackHealthCheckReadPayloadSchema,
  VibeAuditDeploymentRolledBackCrashLoopReadPayloadSchema,
  VibeAuditDeploymentRolledBackFailedSmokeReadPayloadSchema,
  VibeAuditBuildJobRequeuedReadPayloadSchema,
  VibeAuditCostSafetyAutoSuspendedReadPayloadSchema,
  VibeAuditCostSafetySoftLimitWarningReadPayloadSchema,
  VibeAuditCostSafetyManuallySuspendedReadPayloadSchema,
  VibeAuditCostSafetyManuallyWarnedReadPayloadSchema,
  VibeAuditCostSafetyManuallyResumedReadPayloadSchema,
  VibeAuditCapacityRequestedReadPayloadSchema,
  VibeAuditCapacityApprovedReadPayloadSchema,
  VibeAuditCapacityRejectedReadPayloadSchema,
  VibeAuditCapacityExpiredReadPayloadSchema,
  VibeAuditCapacityGrownReadPayloadSchema,
  VibeAuditAppEdgeUnroutedReadPayloadSchema,
  VibeAuditSecretValueStagedReadPayloadSchema,
  VibeAuditSecretValueWrittenReadPayloadSchema,
  VibeAuditGitPushNoDeployReadPayloadSchema,
  VibeAuditCardGrantIssuedReadPayloadSchema,
  VibeAuditCardGrantPausedReadPayloadSchema,
  VibeAuditCardGrantActivatedReadPayloadSchema,
  VibeAuditCardGrantRevokedReadPayloadSchema,
  VibeAuditCardBindingRenamedReadPayloadSchema,
  VibeAuditCardBindingRemovedReadPayloadSchema
] as const;
